// Deep per-route inter-city logistics. Way richer than transport.js — this
// powers the TravelDayPlanner which scripts the entire door-to-door day.
//
// Each entry includes:
//   - Origin station(s) + which to pick
//   - Specific platform / booking URL
//   - Recommended train/flight numbers or companies
//   - Bag-storage locations near origin (real businesses with addresses + costs)
//   - In-transit tips (which side for views, food/drink on board, validation rules)
//   - Arrival transit (taxi cost + alt public transit + walk time)
//   - Recommended buffer windows around the trip
//   - Day-of-week / time-of-year considerations
//
// Portugal is the most detailed entry since that's our case-study trip.

export const ROUTE_LOGISTICS = {
  // ── PORTUGAL — LISBON ↔ PORTO (most detailed; case-study trip) ──
  'Lisbon|Porto': {
    primaryMode: 'train',
    duration: '2h45m - 3h15m',
    cost: '€32-45/pp (1st class) · €25-32/pp (comfort)',
    company: 'Comboios de Portugal (CP)',
    bookingUrl: 'https://www.cp.pt/passageiros/en/buy-tickets',
    bookingLeadTime: 'Book at least 7 days ahead — fares double at the station',
    refundable: 'CP Conforto class is refundable with 24h notice (€7 fee). Promo fares non-refundable.',
    originStations: [
      { name: 'Lisbon Santa Apolónia', code: 'SAS', notes: 'Main intercity station — older, character-filled, near Alfama' },
      { name: 'Lisbon Oriente', code: 'ORI', notes: 'Modern station near airport — convenient if you flew in same morning' },
    ],
    destStations: [
      { name: 'Porto Campanhã', code: 'CMP', notes: 'Main intercity station — 8 min taxi to Ribeira / city center' },
      { name: 'Porto São Bento', code: 'SBR', notes: 'City-center station, but most Lisbon trains stop at Campanhã first' },
    ],
    primaryRoute: 'Santa Apolónia → Campanhã on Alfa Pendular express',
    recommendedTrains: [
      { number: 'AP 131', dep: '08:00', arr: '10:50', notes: 'Early morning — quietest service' },
      { number: 'AP 125', dep: '13:00', arr: '15:50', notes: 'Afternoon classic — good after a relaxed Lisbon morning' },
      { number: 'AP 121', dep: '16:00', arr: '18:50', notes: 'Late afternoon — arrive Porto in time for dinner' },
      { number: 'AP 119', dep: '18:00', arr: '20:50', notes: 'Evening — only if you have a late checkout in Lisbon' },
    ],
    seatTip: 'For the first 30 min after Santa Apolónia, sit on the RIGHT for Tagus river views. After that either side is fine.',
    bagStorage: [
      { name: 'Bounce — Rua da Madalena 80', distFromStation: '5 min walk from Santa Apolónia', cost: '€6/bag/day', url: 'https://usebounce.com' },
      { name: 'Radical Storage — multiple Alfama locations', distFromStation: '10 min walk from Santa Apolónia', cost: '€5/bag/day', url: 'https://radicalstorage.com' },
      { name: 'Station luggage lockers', distFromStation: 'In Santa Apolónia and Oriente', cost: '€4-8/locker', notes: 'First-come, often full mid-day Sat/Sun' },
    ],
    inTransitTips: [
      'Comfort class is fine for a 3h ride — 1st class adds power outlets at every seat + complimentary water',
      'Bar car (carruagem-bar) sells coffee, vinho verde €4/quartino, pasteis de nata €1.50 — bring cash, card sometimes finicky',
      'Free WiFi on Alfa Pendular (slow but works for messaging)',
      'No validation needed — your e-ticket on phone is checked by conductor mid-trip',
    ],
    arrivalTransit: {
      taxi: { cost: '€10-12', time: '8 min', notes: 'White taxis line up outside Campanhã — use meter, never agree flat rate' },
      metro: { cost: '€2.50 single + €0.60 Andante Azul card', time: '20 min', notes: 'Line E (purple) to Trindade then connect to your hotel area. Buy Andante card at machine' },
      walk: { cost: 'free', time: '40-50 min', notes: 'Only walk if your hotel is in Bonfim / Sé district' },
    },
    buffers: {
      hotelDeparture: 'Leave Lisbon hotel 90 min before train (taxi 20 min + bag storage drop-off + station 30 min before departure)',
      airportArrival: 'N/A — train route, not airport',
      hotelCheckin: 'Most Porto hotels are 3 PM. Arriving 2-3 PM: hotel will store bags, refresh in lobby. Some Hyatts offer Globalist 4 PM checkout, ask',
    },
    dayOfWeekNotes: 'Weekends + Sept harvest weekends sell out. Book Friday afternoon + Sunday evening trains 14+ days ahead.',
  },

  // Lisbon ↔ Algarve (Faro) — common Portugal extension
  'Lisbon|Algarve': {
    primaryMode: 'train_or_drive',
    duration: '3h train · 2.5h drive',
    cost: '€22-32/pp train · €60-80 rental car/day',
    company: 'Comboios de Portugal (CP) — Intercidades line',
    bookingUrl: 'https://www.cp.pt/passageiros/en/buy-tickets',
    bookingLeadTime: 'Book 7-14 days ahead in summer/fall',
    originStations: [
      { name: 'Lisbon Oriente', code: 'ORI', notes: 'All Algarve trains depart from Oriente (NOT Santa Apolónia)' },
    ],
    destStations: [
      { name: 'Faro', code: 'FAO', notes: 'Algarve regional hub — taxi to most resort towns' },
      { name: 'Tunes', code: 'TUN', notes: 'Closer to Albufeira / Lagoa — change at Tunes for branch line' },
    ],
    primaryRoute: 'Oriente → Faro on Intercidades',
    seatTip: 'Either side works — landscape is similar both directions',
    bagStorage: [
      { name: 'Lisbon Oriente lockers', distFromStation: 'In station', cost: '€4-8/locker' },
    ],
    inTransitTips: [
      'Bring snacks — bar car selection is minimal on this line',
      'WiFi unreliable — download Netflix/podcasts ahead',
    ],
    arrivalTransit: {
      taxi: { cost: '€10-15 in Faro · €25-40 to Albufeira', time: 'varies', notes: 'Most Algarve resorts are 20-45 min from Faro' },
    },
    buffers: {
      hotelDeparture: 'Leave Lisbon hotel 90 min before Oriente departure (Oriente is by airport, fast metro from city)',
    },
    dayOfWeekNotes: 'Friday + Saturday southbound very busy. Sunday evening northbound = locals returning to Lisbon.',
  },

  // Porto ↔ Douro Valley (Pinhão) — wine country day trip
  'Porto|Douro Valley': {
    primaryMode: 'train_scenic',
    duration: '2h15m one-way',
    cost: '€14/pp Comboio Histórico (vintage steam train) · €7/pp regular',
    company: 'Comboios de Portugal (CP) — Linha do Douro',
    bookingUrl: 'https://www.cp.pt/passageiros/en/buy-tickets',
    bookingLeadTime: 'Vintage steam train (Sat only, summer-fall) sells out 30 days ahead. Regular trains: same-day OK.',
    originStations: [
      { name: 'Porto São Bento', code: 'SBR', notes: 'City-center station — also worth a visit for the azulejos' },
      { name: 'Porto Campanhã', code: 'CMP', notes: 'Alternative — most Douro trains stop here too' },
    ],
    destStations: [
      { name: 'Pinhão', code: 'PNH', notes: 'Heart of port wine country, walk to quintas' },
      { name: 'Régua (Peso da Régua)', code: 'PRG', notes: 'Larger town, better restaurants, museum of the Douro' },
    ],
    primaryRoute: 'São Bento → Pinhão via the Linha do Douro — one of the world\'s most beautiful train rides',
    seatTip: 'RIGHT side after Régua for the river views. Worth standing in the corridor between stops to switch sides.',
    bagStorage: [
      { name: 'Porto São Bento lockers', distFromStation: 'In station', cost: '€4/locker' },
    ],
    inTransitTips: [
      'This is THE scenic ride — sit by the window, don\'t miss the gorge after Régua',
      'No food/drink on regular trains (just the vintage steam one)',
      'Quintas (wineries) along the river are walkable from Pinhão — Quinta do Bomfim, Quinta Nova, Quinta de la Rosa',
    ],
    arrivalTransit: {
      walk: { cost: 'free', time: '5-30 min', notes: 'Most major quintas are walkable from Pinhão station' },
      shuttle: { cost: '€10-15', notes: 'Some quintas (Quinta Nova) run pickup shuttles — confirm with quinta when booking' },
    },
    buffers: {
      hotelDeparture: 'Day trip — leave Porto hotel by 8:30 AM for full Douro day',
    },
    dayOfWeekNotes: 'Saturdays = vintage steam train (worth planning around). Most quintas open Mon-Sat, some closed Sundays.',
  },

  // ── OTHER COMMON EUROPEAN ROUTES (lighter detail, can deepen later) ──
  'Paris|London': {
    primaryMode: 'train',
    duration: '2h15m',
    cost: '€80-200/pp (Eurostar)',
    company: 'Eurostar',
    bookingUrl: 'https://www.eurostar.com',
    bookingLeadTime: '30+ days ahead for best fares. Standard Premier ~€150 advance, €300+ day-of.',
    originStations: [
      { name: 'Paris Gare du Nord', code: 'PNO', notes: 'Eurostar departs from a dedicated terminal — arrive 60 min ahead for security + UK border check' },
    ],
    destStations: [
      { name: 'London St Pancras International', code: 'STP', notes: 'Central London — direct tube to anywhere' },
    ],
    seatTip: 'Coach 5-7 are quieter (away from the family/group end of the train)',
    bagStorage: [
      { name: 'Nannybag — Paris-wide', distFromStation: 'Multiple locations', cost: '€6-8/bag/day', url: 'https://www.nannybag.com' },
    ],
    inTransitTips: [
      'Standard Premier includes light meal + drinks',
      '20 min channel tunnel section — phones lose signal',
      'Arrive at terminal 60-75 min ahead — you clear UK immigration on the Paris side',
    ],
    arrivalTransit: {
      tube: { cost: '£5-7', time: '15-30 min anywhere central', notes: 'King\'s Cross/St Pancras is on the Northern, Piccadilly, Victoria, Metropolitan lines' },
    },
    buffers: { hotelDeparture: 'Leave Paris hotel 90 min before train (45 min transit + 60 min security/border)' },
  },

  'Amsterdam|Paris': {
    primaryMode: 'train',
    duration: '3h15m',
    cost: '€80-180/pp (Thalys / Eurostar)',
    company: 'Eurostar (formerly Thalys)',
    bookingUrl: 'https://www.eurostar.com',
    bookingLeadTime: '14-30 days ahead. Comfort class = good value €100-130.',
    originStations: [
      { name: 'Amsterdam Centraal', code: 'AMS', notes: 'City-center station, direct from anywhere via tram' },
    ],
    destStations: [
      { name: 'Paris Gare du Nord', code: 'PNO', notes: 'Central — easy onward to anywhere in Paris' },
    ],
    seatTip: 'Right side for the Belgian countryside, left side for Lille suburbs (neither is special)',
    bagStorage: [
      { name: 'Amsterdam Centraal lockers', distFromStation: 'In station', cost: '€7-12/locker' },
    ],
    arrivalTransit: { metro: { cost: '€1.90', time: '15-30 min', notes: 'Lines 4 + 5 from Gare du Nord' } },
    buffers: { hotelDeparture: 'Leave Amsterdam hotel 60 min before train (15-30 min transit + 30 min station)' },
  },

  // Default / unmapped — falls back to a generic template
};

// Returns deep logistics for a city pair. Tries exact bundle-name match,
// falls back to substring match. If no mapped route, returns a minimal
// template so the UI still renders something useful.
export function getRouteLogistics(fromCity, toCity) {
  if (!fromCity || !toCity) return null;
  // Handle bundle names: "Lisbon + Porto" matches "Lisbon" and "Porto"
  const fromParts = fromCity.split(' + ').map(s => s.trim());
  const toParts = toCity.split(' + ').map(s => s.trim());
  for (const f of fromParts) {
    for (const t of toParts) {
      const key = `${f}|${t}`;
      if (ROUTE_LOGISTICS[key]) return ROUTE_LOGISTICS[key];
      const reverse = `${t}|${f}`;
      if (ROUTE_LOGISTICS[reverse]) return { ...ROUTE_LOGISTICS[reverse], _reversed: true };
    }
  }
  // No exact match — return a generic template
  return null;
}
